import React, { useCallback } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'

import EditModalFooter from './EditModalFooter'
import type { PatchFlightItemViewModel } from './models'
import {
  extractEditableEndDateTimeStrings,
  extractEditableStartDateTimeStrings,
  updateFlightPayloadFromViewModel,
} from './utils'
import { formOptions } from '../Common/Fields/config'
import FlightFields from '../Common/FlightFields'

import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import DeleteTripItem from 'tripPlanner/components/TripModal/TripItems/DeleteTripItem'
import { usePatchTripItem } from 'tripPlanner/hooks/api/tripItem'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import type { FlightItem } from 'tripPlanner/types/tripItem'

interface Props {
  item: FlightItem
}

const itemToView = (item: FlightItem): PatchFlightItemViewModel => {
  return {
    type: item.type,
    url: item.url,
    isBooked: item.isBooked,
    notes: item.notes,
    airlineName: item.airlineName,
    flightNumber: item.flightNumber,
    confirmationCode: item.confirmationCode,
    price: item.price,
    ...extractEditableStartDateTimeStrings(item),
    ...extractEditableEndDateTimeStrings(item),
    originAirportCode: item.originAirportCode,
    destinationAirportCode: item.destinationAirportCode,
    startPlace: item.startPlace,
    endPlace: item.endPlace,
  }
}

function EditFlight(props: Props) {
  const { item } = props
  const intl = useIntl()

  const openModal = useOpenModal()
  const { closeModal } = useModalCallbacks()

  const methods = useForm<PatchFlightItemViewModel>({
    ...formOptions,
    defaultValues: itemToView(item),
  })
  const { handleSubmit } = methods

  const { mutate: updateMutate, isLoading } = usePatchTripItem({
    onSuccess: () => {
      closeModal()
    },
  })

  const deleteItem = useCallback(
    () => openModal(DeleteTripItem, { item }, { height: 'auto' }),
    [openModal, item],
  )

  const updateItem = useCallback(
    (viewModel: PatchFlightItemViewModel) => {
      updateMutate({
        tripId: item.tripId,
        tripItemId: item.id,
        tripItem: updateFlightPayloadFromViewModel(viewModel),
      })
    },
    [item, updateMutate],
  )

  const modalTitle = intl.formatMessage({
    id: 'BBXbxH_ueW0Ur7Xy_ngH',
    defaultMessage: 'Edit flight',
    description: 'Modal title when editing a flight item in the trip planner',
  })

  return (
    <>
      <ModalHeader title={modalTitle} onCloseButtonClick={closeModal} />
      <ModalBody>
        <FormProvider {...methods}>
          <ModalContent>
            <FlightFields tripId={item.tripId} />
          </ModalContent>
        </FormProvider>
      </ModalBody>
      <EditModalFooter
        deleteItem={deleteItem}
        onSubmit={handleSubmit(updateItem)}
        isLoading={isLoading}
      />
      {isLoading && <LoadingIndicator floating />}
    </>
  )
}

export default EditFlight
